/**
 * LmsTakeQuiz — Prometric-style secure fullscreen quiz page.
 *
 * Enforcement (Feb 2026 R9):
 *  - Requests document.requestFullscreen() on start
 *  - Logs violations on: visibilitychange, blur, fullscreenchange (exit),
 *    contextmenu, copy/paste, resize (dev-tools proxy)
 *  - Strict mode: 3 violations → auto-submit with auto_reason=violations
 *  - Moderate mode: unlimited but warnings surfaced + violations logged
 *  - Off mode: no enforcement (behaves like a normal quiz)
 *  - Countdown timer syncs to server deadline_at
 *  - beforeunload prompts + also fires a sync sendBeacon violation log
 *
 * Note: browsers cannot fully sandbox a web page. We do what HTML5 allows
 * (fullscreen API, visibility events, key blockers). The violation log
 * + auto-submit gives admins high-confidence detection of cheating.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Clock, ShieldAlert, Lock, ChevronLeft, ChevronRight, Send, Maximize } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const STRICT_MAX_VIOLATIONS = 3;

function fmtDur(secs) {
  if (secs < 0) secs = 0;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function LmsTakeQuiz() {
  const { quizId } = useParams();
  const { activeWorkspaceId } = useAuth();
  const wsId = activeWorkspaceId;
  const [sp] = useSearchParams();
  const enrollmentId = sp.get("enrollment_id") || null;
  const navigate = useNavigate();

  const [phase, setPhase] = useState("intro"); // intro | live | submitting | done
  const [session, setSession] = useState(null); // {attempt_id, session_token, quiz, questions, deadline_at, max_score}
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(null); // seconds
  const [violationCount, setViolationCount] = useState(0);
  const [warning, setWarning] = useState(null);
  const startedAtRef = useRef(null);
  const attemptRef = useRef(null); // keep live refs for beacons
  const answersRef = useRef({});
  answersRef.current = answers;

  const strict = session?.quiz?.prometric_mode === "strict";
  const moderate = session?.quiz?.prometric_mode === "moderate";
  const enforce = strict || moderate;

  // ─── Start attempt ──────────────────────────────────────────────
  const start = useCallback(async () => {
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/lms/quizzes/${quizId}/attempts`,
        { enrollment_id: enrollmentId });
      setSession(data);
      attemptRef.current = data;
      startedAtRef.current = Date.now();
      if (data.deadline_at) {
        const dl = new Date(data.deadline_at).getTime();
        setRemaining(Math.floor((dl - Date.now()) / 1000));
      }
      setPhase("live");
      // Request fullscreen for strict + moderate
      if ((data.quiz?.prometric_mode || "off") !== "off") {
        try {
          await document.documentElement.requestFullscreen({ navigationUI: "hide" });
        } catch (_e) { /* fullscreen requires user gesture in some browsers */ }
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not start quiz"));
    }
  }, [wsId, quizId, enrollmentId]);

  // ─── Timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "live" || remaining === null) return;
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r === null) return null;
        if (r <= 1) {
          clearInterval(t);
          submitAttempt("timeout");
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [phase, remaining !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Submit ─────────────────────────────────────────────────────
  const submitAttempt = useCallback(async (auto_reason = null) => {
    if (phase !== "live") return;
    setPhase("submitting");
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/lms/attempts/${attemptRef.current.attempt_id}/submit`,
        { answers: answersRef.current, auto_reason });
      // Exit fullscreen if we entered it
      try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (e) { /* noop */ }
      setPhase("done");
      navigate(`/app/lms/attempts/${attemptRef.current.attempt_id}/result`, {
        state: { result: data, quiz: attemptRef.current.quiz },
      });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Submit failed"));
      setPhase("live");
    }
  }, [wsId, navigate, phase]);

  // ─── Violation logger ────────────────────────────────────────────
  const logViolation = useCallback(async (kind, meta) => {
    if (!attemptRef.current || phase !== "live") return;
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/lms/attempts/${attemptRef.current.attempt_id}/violation`,
        { kind, meta });
      const count = data.violation_count || 0;
      setViolationCount(count);
      setWarning({
        kind,
        text: `${kindLabel(kind)} detected · violation ${count}${strict ? ` of ${STRICT_MAX_VIOLATIONS}` : ""}`,
      });
      setTimeout(() => setWarning(null), 4000);
      if (strict && count >= STRICT_MAX_VIOLATIONS) {
        toast.error("Quiz auto-submitted due to repeated violations");
        submitAttempt("violations");
      }
    } catch (_e) { /* violation logging is best-effort */ }
  }, [wsId, phase, strict, submitAttempt]);

  // ─── Attach enforcement listeners ────────────────────────────────
  useEffect(() => {
    if (phase !== "live" || !enforce) return;

    const onVis = () => {
      if (document.hidden) logViolation("tab_switch");
    };
    const onBlur = () => logViolation("blur");
    const onFsChange = () => {
      if (!document.fullscreenElement) logViolation("fullscreen_exit");
    };
    const onCtx = (e) => { e.preventDefault(); logViolation("context_menu"); };
    const onCopy = (e) => { e.preventDefault(); logViolation("copy"); };
    const onPaste = (e) => { e.preventDefault(); logViolation("paste"); };
    const onKey = (e) => {
      // Block ctrl+c/v/x/a and F12, ctrl+shift+i (devtools)
      if ((e.ctrlKey || e.metaKey) && ["c", "v", "x", "a", "p", "s"].includes(e.key.toLowerCase())) {
        e.preventDefault();
        logViolation("copy", { key: e.key });
      }
      if (e.key === "F12" || (e.ctrlKey && e.shiftKey && ["i", "j", "c"].includes(e.key.toLowerCase()))) {
        e.preventDefault();
        logViolation("devtools_suspected", { key: e.key });
      }
    };
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Leaving will end your quiz attempt.";
      // Best-effort violation log via beacon
      try {
        const payload = JSON.stringify({ kind: "other", meta: { reason: "unload" } });
        navigator.sendBeacon(
          `${api.defaults.baseURL}/workspaces/${wsId}/lms/attempts/${attemptRef.current.attempt_id}/violation`,
          new Blob([payload], { type: "application/json" }));
      } catch (_e) { /* beacon best-effort */ }
      return e.returnValue;
    };

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("copy", onCopy);
    document.addEventListener("paste", onPaste);
    document.addEventListener("keydown", onKey);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [phase, enforce, logViolation, wsId]);

  // ─── Answer handlers ────────────────────────────────────────────
  const setAns = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  // ═══════════════════ Render ════════════════════════════════════
  if (phase === "intro") {
    return <IntroScreen wsId={wsId} quizId={quizId} enrollmentId={enrollmentId} onStart={start} />;
  }
  if (phase === "submitting") {
    return <div className="min-h-screen flex items-center justify-center text-lg">Submitting your attempt…</div>;
  }
  if (!wsId) return <div className="p-6">Loading workspace…</div>;
  if (!session) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const q = session.questions[idx];
  const answered = Object.keys(answers).filter((k) => {
    const v = answers[k];
    return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  }).length;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 select-none" data-testid="lms-take-quiz-live">
      {/* Top bar */}
      <div className="sticky top-0 border-b border-neutral-800 bg-neutral-900/95 backdrop-blur px-4 py-2 flex items-center gap-3">
        <ShieldAlert className="w-4 h-4 text-amber-400" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{session.quiz.title}</div>
          <div className="text-[10px] text-neutral-400">
            {session.quiz.prometric_mode === "strict"
              ? "Strict proctoring · violations auto-submit after 3 breaches"
              : session.quiz.prometric_mode === "moderate"
              ? "Moderate proctoring · violations are logged"
              : "Open mode"}
          </div>
        </div>
        {remaining !== null && (
          <div className={`flex items-center gap-1 font-mono text-lg ${remaining < 60 ? "text-red-400" : "text-neutral-100"}`} data-testid="lms-quiz-timer">
            <Clock className="w-4 h-4" /> {fmtDur(remaining)}
          </div>
        )}
        {enforce && (
          <div className="flex items-center gap-1 text-xs" data-testid="lms-quiz-violations">
            <Lock className="w-3 h-3" />
            {violationCount}{strict ? `/${STRICT_MAX_VIOLATIONS}` : ""} violations
          </div>
        )}
      </div>

      {warning && (
        <div className="fixed top-14 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-4 py-2 shadow-lg flex items-center gap-2 animate-pulse" data-testid="lms-quiz-warning">
          <AlertTriangle className="w-4 h-4" /> {warning.text}
        </div>
      )}

      {/* Body */}
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-0 min-h-[calc(100vh-52px)]">
        {/* Left question list */}
        <aside className="border-r border-neutral-800 bg-neutral-900/40 p-2 overflow-auto max-h-[calc(100vh-52px)]">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            {answered} / {session.questions.length} answered
          </div>
          <div className="grid grid-cols-5 md:grid-cols-4 gap-1">
            {session.questions.map((qq, i) => {
              const v = answers[qq.id];
              const isAns = v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
              return (
                <button
                  key={qq.id}
                  onClick={() => setIdx(i)}
                  className={`h-8 text-xs font-mono border ${idx === i ? "border-emerald-400 bg-emerald-500/20" : isAns ? "border-neutral-700 bg-neutral-800" : "border-neutral-700"}`}
                  data-testid={`lms-quiz-qbtn-${i}`}
                >{i + 1}</button>
              );
            })}
          </div>
          <Button
            className="w-full mt-3 rounded-none bg-emerald-600 hover:bg-emerald-500"
            onClick={() => submitAttempt("manual")}
            data-testid="lms-quiz-submit"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Submit ({answered}/{session.questions.length})
          </Button>
        </aside>

        {/* Question body */}
        <main className="p-6 max-w-3xl mx-auto w-full">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Question {idx + 1} of {session.questions.length} · {q.points} pt{q.points === 1 ? "" : "s"}</div>
          <h2 className="text-xl font-semibold mb-2" data-testid="lms-quiz-qtext">{q.text}</h2>
          {q.description && <p className="text-sm text-neutral-400 mb-3">{q.description}</p>}
          <QuestionInput q={q} value={answers[q.id]} onChange={(v) => setAns(q.id, v)} />

          <div className="mt-6 flex items-center gap-2">
            <Button variant="outline" className="rounded-none border-neutral-700 text-neutral-100" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <Button variant="outline" className="rounded-none border-neutral-700 text-neutral-100" onClick={() => setIdx((i) => Math.min(session.questions.length - 1, i + 1))} disabled={idx === session.questions.length - 1}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
}

// ─── UI helpers ────────────────────────────────────────────────────

function kindLabel(k) {
  return ({
    tab_switch: "Tab switch",
    blur: "Window lost focus",
    fullscreen_exit: "Fullscreen exit",
    context_menu: "Right-click blocked",
    copy: "Copy blocked",
    paste: "Paste blocked",
    devtools_suspected: "Devtools shortcut blocked",
    resize: "Window resize",
  })[k] || "Violation";
}

function IntroScreen({ wsId, quizId, enrollmentId, onStart }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    (async () => {
      // Peek at the quiz metadata via the manage view — falls back to
      // hard-coded default hints if the caller lacks manage perm.
      try {
        const { data } = await api.get(`/workspaces/${wsId}/lms/quizzes/${quizId}/questions`);
        setMeta({ question_count: data.length });
      } catch {
        setMeta({ question_count: null });
      }
    })();
  }, [wsId, quizId]);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center p-6">
      <div className="max-w-lg w-full border border-neutral-800 p-6 space-y-4">
        <div className="flex items-center gap-2 text-amber-400">
          <ShieldAlert className="w-5 h-5" />
          <h1 className="text-xl font-semibold">Secure Assessment</h1>
        </div>
        <p className="text-sm text-neutral-300">
          You&apos;re about to begin a proctored assessment. On start, your browser will enter fullscreen and monitoring will activate.
        </p>
        <ul className="text-sm text-neutral-400 space-y-1 list-disc pl-4">
          <li>Do not switch tabs, minimize, or exit fullscreen.</li>
          <li>Copy, paste, right-click and devtools shortcuts are blocked.</li>
          <li>Strict mode: 3 violations will auto-submit your attempt.</li>
          <li>Timer, if enabled, continues even if you close the tab.</li>
        </ul>
        {meta?.question_count != null && (
          <div className="text-xs text-neutral-500">{meta.question_count} question{meta.question_count === 1 ? "" : "s"} in this quiz.</div>
        )}
        <Button className="w-full rounded-none bg-emerald-600 hover:bg-emerald-500" onClick={onStart} data-testid="lms-quiz-start">
          <Maximize className="w-4 h-4 mr-1" /> I understand — start
        </Button>
      </div>
    </div>
  );
}

function QuestionInput({ q, value, onChange }) {
  if (q.type === "mcq" || q.type === "truefalse") {
    return (
      <div className="space-y-2">
        {q.options.map((o) => (
          <label key={o.key} className={`block border p-3 cursor-pointer ${value === o.key ? "border-emerald-400 bg-emerald-500/10" : "border-neutral-700"}`}>
            <input type="radio" name={q.id} className="mr-2" checked={value === o.key} onChange={() => onChange(o.key)} data-testid={`lms-quiz-opt-${q.id}-${o.key}`} />
            <span className="font-mono text-xs text-neutral-500 mr-1">{o.key}.</span>{o.label}
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "multi") {
    const v = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        {q.options.map((o) => (
          <label key={o.key} className={`block border p-3 cursor-pointer ${v.includes(o.key) ? "border-emerald-400 bg-emerald-500/10" : "border-neutral-700"}`}>
            <input type="checkbox" className="mr-2" checked={v.includes(o.key)} onChange={(e) => {
              onChange(e.target.checked ? [...v, o.key] : v.filter((x) => x !== o.key));
            }} data-testid={`lms-quiz-opt-${q.id}-${o.key}`} />
            <span className="font-mono text-xs text-neutral-500 mr-1">{o.key}.</span>{o.label}
          </label>
        ))}
      </div>
    );
  }
  return (
    <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} className="rounded-none bg-neutral-900 border-neutral-700 text-neutral-100" data-testid={`lms-quiz-text-${q.id}`} placeholder="Type your answer" />
  );
}
